import { Link } from 'react-router-dom';
import { nip19 } from 'nostr-tools';
import { BookOpen, Trash2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useOfflinePublications } from '@/hooks/useOfflinePublications';
import { sanitizeUrl } from '@/lib/sanitizeUrl';
import { getTagValue } from '@/lib/nip54';
import { publicationSummary, publicationTitle, publicationType } from '@/lib/nkbip01';
import type { OfflinePublication } from '@/lib/offlineLibrary';

interface OfflinePublicationCardProps {
  publication: OfflinePublication;
}

/**
 * A saved-for-offline publication card.
 *
 * Deliberately does not reuse `PublicationCard`: that card resolves the
 * author via `useAuthor` (a relay query) and renders a `StarButton`
 * (which queries and publishes). Neither works without a connection —
 * the situation this card exists for. The author comes from the
 * snapshot captured at save time, the section count from the sections
 * actually stored (not the index's `a` tag count, so it reflects what's
 * genuinely readable offline), and the only action is removal.
 */
export function OfflinePublicationCard({ publication }: OfflinePublicationCardProps) {
  const { remove } = useOfflinePublications();
  const { index, sections, author, savedAt } = publication;

  const slug = getTagValue(index, 'd') ?? '';
  const npub = nip19.npubEncode(index.pubkey);
  const displayName = author?.name ?? `${index.pubkey.slice(0, 8)}…`;
  const summary = publicationSummary(index);
  const image = sanitizeUrl(getTagValue(index, 'image'));
  const picture = sanitizeUrl(author?.picture);
  const href = `/p/${encodeURIComponent(slug)}/${npub}`;

  return (
    <Card className="flex flex-col overflow-hidden transition-shadow hover:shadow-md">
      {image && (
        <Link to={href} className="block">
          <img src={image} alt="" className="h-32 w-full object-cover" loading="lazy" />
        </Link>
      )}
      <CardHeader>
        <div className="mb-1 flex flex-wrap items-center gap-2">
          <Badge variant="secondary" className="capitalize">
            <BookOpen className="size-3" />
            {publicationType(index)}
          </Badge>
          <span className="text-xs text-muted-foreground">
            {sections.length} {sections.length === 1 ? 'section' : 'sections'} saved
          </span>
        </div>
        <CardTitle className="text-lg">
          <Link to={href} className="hover:underline">
            {publicationTitle(index)}
          </Link>
        </CardTitle>
      </CardHeader>
      <CardContent className="flex flex-1 flex-col justify-end space-y-3">
        {summary && <p className="line-clamp-3 text-sm text-muted-foreground break-words">{summary}</p>}
        <div className="flex items-center justify-between gap-2">
          <div className="flex min-w-0 items-center gap-2 text-xs text-muted-foreground">
            <Avatar className="size-5">
              <AvatarImage src={picture} alt={displayName} />
              <AvatarFallback>{displayName.charAt(0).toUpperCase()}</AvatarFallback>
            </Avatar>
            <span className="truncate">{displayName}</span>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="size-8 shrink-0 text-muted-foreground hover:text-destructive"
            onClick={() => remove(index)}
            title="Remove from offline library"
            aria-label={`Remove "${publicationTitle(index)}" from offline library`}
          >
            <Trash2 className="size-4" />
          </Button>
        </div>
        {savedAt > 0 && (
          <p className="text-xs text-muted-foreground/70">
            Saved <time dateTime={new Date(savedAt).toISOString()}>{new Date(savedAt).toLocaleDateString()}</time>
          </p>
        )}
      </CardContent>
    </Card>
  );
}
